import { FC } from 'react';
import ReadonlyView from '@/components/form/readonly-view';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import { PublicServiceModel } from '@/lib/models/public-service/public-service.model';
import Chip from '@/components/chip';
import { Box, Stack } from '@mui/material';
import Text from '@/components/text';
import Icon from '@/components/icon';
import NoneChip from '@/components/chip/none-chip';
import IntercomButton from '@/components/intercom/intercom-button';
import RiskChip from '@/components/chip/risk-chip';

interface Props {
  publicService: PublicServiceModel;
}

const readOnlyFields: ReadonlyField[] = [
  { label: 'Risk Level', prop: 'risk', rowGroup: 'risk' },
  { label: 'Risk Confidence', prop: 'signalConfidence', rowGroup: 'risk' },
  { label: '', prop: 'riskCalculationLink', rowGroup: 'risk' },
  { label: 'Trains on User Data', prop: 'trainOnUserData' },
  { label: 'Unmoderated', prop: 'noModeration' },
];

const getReadonlyData = (data: PublicServiceModel | null) => {
  if (!data) {
    return {};
  }

  const { signals, confidenceOption, riskLevel } = data;
  const NaChipEl = <NoneChip notAvailable />;
  const YesChipEl = <Chip label="Yes" color="error" />;
  const NoChipEl = <Chip label="No" color="success" />;

  const { trainOnUserData, noModeration } = signals;

  const hasTodSignal = Boolean(trainOnUserData);
  let TodEl = NaChipEl;
  if (hasTodSignal) {
    TodEl = trainOnUserData?.value ? YesChipEl : NoChipEl;
  }

  const hasModSignal = Boolean(noModeration);
  let ModEl = NaChipEl;
  if (hasModSignal) {
    ModEl = noModeration?.value ? YesChipEl : NoChipEl;
  }

  const { label: conLabel, icon: conIcon } = confidenceOption;

  return {
    risk: <RiskChip value={riskLevel} />,
    trainOnUserData: (
      <Stack gap={2}>
        <Box>{TodEl}</Box>
        {hasTodSignal && trainOnUserData.description && <Box>{trainOnUserData.description}</Box>}
      </Stack>
    ),
    noModeration: (
      <Stack gap={2}>
        <Box>{ModEl}</Box>
        {hasModSignal && noModeration.description && <Box>{noModeration.description}</Box>}
      </Stack>
    ),
    signalConfidence: (
      <Stack direction="row" gap={0.5}>
        <Icon name={conIcon} />
        <Text>{conLabel}</Text>
      </Stack>
    ),
    riskCalculationLink: (
      <Box paddingY={1}>
        <IntercomButton intercomArticle="public-services" label="Learn How Risk Is Calculated" />
      </Box>
    ),
  };
};

export const Risk: FC<Props> = ({ publicService }) => {
  return <ReadonlyView fields={readOnlyFields} data={getReadonlyData(publicService)} />;
};

export default Risk;
